// Map backend `ActivityAction` codes to the coarser categories rendered by
// the activity-history modal's left sidebar. The categories are a UX-only
// grouping (no wire equivalent) so we keep the mapping in the frontend.
//
// Categories:
//   - PRICE       → PRICE_CHANGED
//   - STATUS      → STATUS_CHANGED
//   - IMAGES      → IMAGES_UPLOADED + IMAGES_DELETED + IMAGES_REORDERED
//   - FRESHNESS   → CONFIRMED_FRESH
//   - APPOINTMENT → APPOINTMENT_* (CREATED / RESCHEDULED / STATUS / CANCELED)
//   - INFO        → INFO_UPDATED + BULK_IMPORTED
//   - OTHER       → everything else (CREATED, DELETED, lead/customer actions, ...)
//
// "ALL" is the implicit no-filter bucket; it never appears in the map.

export type ActivityCategoryKey =
  | "ALL"
  | "PRICE"
  | "STATUS"
  | "IMAGES"
  | "FRESHNESS"
  | "APPOINTMENT"
  | "INFO"
  | "OTHER";

export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategoryKey, string> = {
  ALL: "Tất cả",
  PRICE: "Giá",
  STATUS: "Trạng thái",
  IMAGES: "Ảnh",
  FRESHNESS: "Xác nhận tin",
  APPOINTMENT: "Lịch hẹn",
  INFO: "Sửa thông tin",
  OTHER: "Khác",
};

/**
 * Order in which non-ALL categories render in the sidebar. ALL is always
 * pinned to the top by the consumer, so it is omitted from this list.
 */
export const ACTIVITY_CATEGORY_ORDER: readonly ActivityCategoryKey[] = [
  "PRICE",
  "STATUS",
  "IMAGES",
  "FRESHNESS",
  "APPOINTMENT",
  "INFO",
  "OTHER",
];

/**
 * Saturated dot colours per category. Used by the activity-history modal's
 * sidebar filter list so each row reads at a glance — amber for Giá, sky
 * for Trạng thái, pink for Ảnh, etc. Matches the design comp's palette.
 */
export const ACTIVITY_CATEGORY_DOT_COLORS: Record<ActivityCategoryKey, string> = {
  ALL: "bg-slate-400",
  PRICE: "bg-amber-500",
  STATUS: "bg-sky-500",
  IMAGES: "bg-pink-500",
  FRESHNESS: "bg-emerald-600",
  APPOINTMENT: "bg-purple-500",
  INFO: "bg-slate-500",
  OTHER: "bg-zinc-400",
};

/**
 * Project a backend action code onto one of {@link ActivityCategoryKey}.
 * Unknown codes fall through to "OTHER" so unmapped backend additions
 * remain visible in the modal until they're explicitly bucketed.
 */
export function categorize(action: string): ActivityCategoryKey {
  switch (action) {
    case "PRICE_CHANGED":
      return "PRICE";
    case "STATUS_CHANGED":
      return "STATUS";
    case "IMAGES_UPLOADED":
    case "IMAGES_DELETED":
    case "IMAGES_REORDERED":
      return "IMAGES";
    case "CONFIRMED_FRESH":
      return "FRESHNESS";
    case "APPOINTMENT_CREATED":
    case "APPOINTMENT_RESCHEDULED":
    case "APPOINTMENT_STATUS_CHANGED":
    case "APPOINTMENT_CANCELED":
      return "APPOINTMENT";
    case "INFO_UPDATED":
    case "BULK_IMPORTED":
      return "INFO";
    default:
      return "OTHER";
  }
}
